import {
  ArcElement,
  BarElement,
  CategoryScale,
  ChartArea,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  Plugin,
  PointElement,
  ScriptableContext,
  Tooltip,
  TooltipLabelStyle,
} from 'chart.js'
import { getCssRgbValue } from 'shared/utils/get-css-rgb-value'

export type DashboardChartType = 'bar' | 'line'

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
)

const getLineGradient = ({ chart }: ScriptableContext<'line'>) => {
  const { ctx, chartArea } = chart
  const color = getCssRgbValue('--color-icon-accent', 0.1)

  if (!chartArea) return color

  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)

  gradient.addColorStop(0, color)
  gradient.addColorStop(1, getCssRgbValue('--color-bg-primary', 0))

  return gradient
}

/*
 * Marker of the hovered point: an accent core inside a white ring that casts a soft shadow. Chart.js
 * point elements carry no shadow option, so the marker is drawn here and the point element itself
 * stays invisible.
 */
const MARKER_RADIUS = 5

const MARKER_RING_RADIUS = 9

const MARKER_SHADOW_COLOR = 'rgba(50, 50, 71, 0.36)'

const MARKER_SHADOW_OFFSET_Y = 2

const MARKER_SHADOW_BLUR = 2

/*
 * The canvas clips the marker at its edge, and the last point sits on the chart area's right edge.
 * Chart.js leaves room there only for an x-axis label, and a narrow chart can skip that label: twelve
 * months on a phone keep every other one, which drops the last. The padding keeps the ring and its
 * shadow whole either way, and adds nothing where the label already leaves more room.
 */
const MARKER_CLEARANCE = MARKER_RING_RADIUS + MARKER_SHADOW_BLUR

const drawActiveMarker = (
  ctx: CanvasRenderingContext2D,
  { x, y, pixelRatio }: { pixelRatio: number; x: number; y: number },
) => {
  ctx.save()
  /*
   * Canvas leaves shadow offsets and blur out of the current transform, so on a retina canvas they
   * have to be scaled by hand to land on the sizes the design asks for.
   */
  ctx.shadowColor = MARKER_SHADOW_COLOR
  ctx.shadowOffsetY = MARKER_SHADOW_OFFSET_Y * pixelRatio
  ctx.shadowBlur = MARKER_SHADOW_BLUR * pixelRatio
  ctx.fillStyle = getCssRgbValue('--color-bg-primary')
  ctx.beginPath()
  ctx.arc(x, y, MARKER_RING_RADIUS, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  ctx.save()
  ctx.fillStyle = getCssRgbValue('--color-icon-accent')
  ctx.beginPath()
  ctx.arc(x, y, MARKER_RADIUS, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

export const activePointPlugin: Plugin<'line'> = {
  id: 'activePoint',
  afterDatasetsDraw: chart => {
    const activeElement = chart.getActiveElements()[0]

    if (!activeElement) return

    const { ctx, chartArea } = chart
    const { x, y } = activeElement.element

    ctx.save()
    ctx.beginPath()
    ctx.setLineDash([4, 4])
    ctx.moveTo(x, chartArea.top)
    ctx.lineTo(x, chartArea.bottom)
    ctx.strokeStyle = getCssRgbValue('--color-border-300')
    ctx.stroke()
    ctx.restore()

    drawActiveMarker(ctx, { x, y, pixelRatio: chart.currentDevicePixelRatio ?? 1 })
  },
}

export const lineChartPlugins: Array<Plugin<'line'>> = [activePointPlugin]

/*
 * Bars follow the charts doc: a day owns an equal share of the plot width, and its bar leaves 30% of
 * that slot as the gap. At 4px and below there is no gap, so bars touch and read as a histogram. On
 * a short period a bar stops at 56px and the gap takes the rest.
 */
const BAR_GAP_RATIO = 0.3

const HISTOGRAM_MAX_SLOT = 4

const MIN_BAR_WIDTH = 1

const MAX_BAR_WIDTH = 56

const BAR_RADIUS = 3

interface BarSlotChart {
  chartArea?: ChartArea
  data: { labels?: unknown[] }
}

const getBarSlot = ({ chartArea, data }: BarSlotChart) => {
  const dayCount = data.labels?.length ?? 0

  return chartArea && dayCount ? chartArea.width / dayCount : 0
}

const snapToDevicePixel = (value: number, pixelRatio: number) =>
  Math.round(value * pixelRatio) / pixelRatio

/* The 1px floor rounded up to whole device pixels, so a floored bar keeps both edges on the grid. */
const getMinBarWidth = (pixelRatio: number) => Math.ceil(MIN_BAR_WIDTH * pixelRatio) / pixelRatio

/* Rounded tops on touching bars make a scalloped edge, so a histogram keeps square corners. */
export const getBarRadius = ({ chart }: { chart: BarSlotChart }) =>
  getBarSlot(chart) > HISTOGRAM_MAX_SLOT ? BAR_RADIUS : 0

/*
 * Chart.js splits each slot evenly between a day's grouped bars (`barChartOptions` sets both
 * percentages to 1), which tells a bar its place in the group, and this lays the group out by the
 * rule. Percentages cannot drop the gap below 4px, keep the 1px floor or cap the width. Each edge is
 * snapped to a device pixel on its own, so touching bars stay free of antialiasing seams.
 *
 * A lone bar keeps the 1px floor even where it spills past a day narrower than that. A group never
 * leaves its day, so no currency covers another date: it widens into the gap to give each bar the
 * floor, and when even the whole slot is too narrow, its bars split the slot down to one device
 * pixel each. They overlap only when the slot has fewer device pixels than the group has bars.
 *
 * Below one device pixel per day no layout keeps dates apart. The bars then still take a device
 * pixel, starting at the pixel their day snaps to, and cover the next date the way a lone bar
 * under 1px does; only grouping days into weeks or months would avoid that.
 */
export const barSlotPlugin: Plugin<'bar'> = {
  id: 'barSlot',
  afterDatasetUpdate: (chart, { meta }) => {
    const slot = getBarSlot(chart)

    if (!slot) return

    const barShare = slot > HISTOGRAM_MAX_SLOT ? 1 - BAR_GAP_RATIO : 1
    const pixelRatio = chart.currentDevicePixelRatio ?? 1
    const minWidth = getMinBarWidth(pixelRatio)
    const snap = (value: number) => snapToDevicePixel(value, pixelRatio)

    meta.data.forEach((element, index) => {
      if (!(element instanceof BarElement)) return

      const { x } = element
      const { width } = element.getProps(['width'])
      const slotLeft = chart.chartArea.left + index * slot
      const groupSize = Math.round(slot / width)
      const position = Math.round((x - width / 2 - slotLeft) / width)
      const isGroup = groupSize > 1
      const ruleWidth = Math.min(slot * barShare, MAX_BAR_WIDTH * groupSize)
      const groupWidth = isGroup
        ? Math.min(Math.max(ruleWidth, groupSize * minWidth), slot)
        : ruleWidth
      const groupLeft = slotLeft + (slot - groupWidth) / 2
      const partWidth = groupWidth / groupSize
      const start = snap(groupLeft + position * partWidth)
      const end = snap(groupLeft + (position + 1) * partWidth)
      const barWidth = Math.max(end - start, isGroup ? 1 / pixelRatio : minWidth)
      const left = Math.max(
        snap(groupLeft),
        Math.min(start, snap(groupLeft + groupWidth) - barWidth),
      )

      Object.assign(element, { x: left + barWidth / 2, width: barWidth })
    })
  },
}

/* The column sits under the gridlines, and chart.js paints those before any `beforeDatasetsDraw`. */
export const activeBarSlotPlugin: Plugin<'bar'> = {
  id: 'activeBarSlot',
  beforeDraw: chart => {
    const [activeElement] = chart.getActiveElements()
    const slot = getBarSlot(chart)

    if (!activeElement || !slot) return

    const { ctx, chartArea } = chart
    const pixelRatio = chart.currentDevicePixelRatio ?? 1
    const slotLeft = chartArea.left + activeElement.index * slot
    const left = snapToDevicePixel(slotLeft, pixelRatio)
    const right = snapToDevicePixel(slotLeft + slot, pixelRatio)
    const width = Math.max(right - left, getMinBarWidth(pixelRatio))

    ctx.save()
    ctx.fillStyle = getCssRgbValue('--color-bg-secondary')
    ctx.fillRect(left, chartArea.top, width, chartArea.height)
    ctx.restore()
  },
}

export const barChartPlugins: Array<Plugin<'bar'>> = [barSlotPlugin, activeBarSlotPlugin]

/*
 * Chart.js paints its own tooltip on the canvas, which cannot carry a shadow, a divider, or mixed
 * text weights. The tooltip is rendered as a DOM node next to the canvas instead, so it matches the
 * design. One node is reused per chart and lives in the canvas wrapper, which `ChartCard` keeps
 * `relative` for that reason.
 */
const TOOLTIP_CLASS_NAME = `bg-bg-primary text-text-secondary border-border-100 shadow-1 rounded-lg border px-3 py-2 text-base font-normal pointer-events-none absolute top-0 left-0 z-10 flex w-max max-w-[240px] flex-col gap-2 opacity-0 transition-[opacity,transform] duration-150 ease-out`

const TOOLTIP_OFFSET = 12

interface ChartTooltipModel {
  body: Array<{ after: string[]; before: string[]; lines: string[] }>
  caretX: number
  caretY: number
  footer: string[]
  labelColors: TooltipLabelStyle[]
  opacity: number
  title: string[]
}

const getTooltipElement = (canvas: HTMLCanvasElement) => {
  const parent = canvas.parentElement

  if (!parent) return null

  const existing = parent.querySelector<HTMLDivElement>('[data-chart-tooltip]')

  if (existing) return existing

  const element = document.createElement('div')

  element.dataset.chartTooltip = ''
  element.className = TOOLTIP_CLASS_NAME
  parent.appendChild(element)

  return element
}

const renderTooltipContent = (
  element: HTMLDivElement,
  tooltip: ChartTooltipModel,
  withMarkers: boolean,
) => {
  element.replaceChildren()

  const [title] = tooltip.title

  if (title) {
    const titleElement = document.createElement('div')

    titleElement.className = 'border-border-100 border-b pb-2 text-center'
    titleElement.textContent = title
    element.appendChild(titleElement)
  }

  /*
   * A row is whatever the chart's `beforeLabel` / `label` / `afterLabel` callbacks returned: the
   * label carries the value and reads as emphasized, everything around it stays muted. Nothing in
   * here composes or parses that text.
   */
  tooltip.body.forEach(({ after, before, lines }, index) => {
    const row = document.createElement('div')

    /* Rows with a color key line up their markers on the left edge, so the series read as a list. */
    row.className = `flex items-center gap-1.5 ${withMarkers ? 'justify-start' : 'justify-center'}`

    if (withMarkers) {
      /*
       * The marker repeats the series color the chart resolved for that element: the bar fill, or
       * the point background a line keeps for this purpose. Anything but a plain color (a gradient
       * fill) falls back to the accent.
       */
      const color = tooltip.labelColors[index]?.backgroundColor
      const marker = document.createElement('span')

      marker.className = 'size-2 shrink-0 rounded-full'
      marker.style.backgroundColor =
        typeof color === 'string' ? color : getCssRgbValue('--color-icon-accent')
      row.appendChild(marker)
    }

    const appendSegments = (segments: string[], className?: string) => {
      segments.filter(Boolean).forEach(segment => {
        const segmentElement = document.createElement('span')

        if (className) segmentElement.className = className

        segmentElement.textContent = segment
        row.appendChild(segmentElement)
      })
    }

    appendSegments(before)
    appendSegments(lines, 'text-text-primary font-medium')
    appendSegments(after)

    if (row.childElementCount) element.appendChild(row)
  })

  const [footer] = tooltip.footer

  if (footer) {
    const footerElement = document.createElement('div')

    footerElement.className = 'border-border-100 border-t pt-2 text-center'
    footerElement.textContent = footer
    element.appendChild(footerElement)
  }
}

/*
 * The node sits at the wrapper origin and every offset goes into one `transform`, so moving to the
 * next point animates a single property instead of `left` and `top`.
 */
const positionTooltip = (
  element: HTMLDivElement,
  chart: { height: number; width: number },
  { caretX, caretY }: ChartTooltipModel,
) => {
  const fitsRight = caretX + TOOLTIP_OFFSET + element.offsetWidth <= chart.width
  const halfHeight = element.offsetHeight / 2
  const top = Math.min(
    Math.max(caretY, halfHeight),
    Math.max(chart.height - halfHeight, halfHeight),
  )
  const left = fitsRight
    ? `${caretX + TOOLTIP_OFFSET}px`
    : `calc(${caretX - TOOLTIP_OFFSET}px - 100%)`

  element.style.transform = `translate(${left}, calc(${top}px - 50%))`
}

/* Reading layout commits the pending transform, so the next one animates from where it landed. */
const flushLayout = (element: HTMLDivElement) => element.getBoundingClientRect()

/* `extra` goes under the content before the tooltip is measured, so it is placed at full size. */
export const externalTooltipHandler = (
  {
    chart,
    tooltip,
  }: {
    chart: {
      canvas: HTMLCanvasElement
      data: { datasets: unknown[] }
      height: number
      width: number
    }
    tooltip: ChartTooltipModel
  },
  extra?: HTMLElement,
) => {
  const element = getTooltipElement(chart.canvas)

  if (!element) return

  if (!tooltip.opacity) {
    element.style.opacity = '0'

    return
  }

  const wasVisible = element.style.opacity === '1'

  /* A single-series chart needs no color key, so its rows drop the marker. */
  renderTooltipContent(element, tooltip, chart.data.datasets.length > 1)

  if (extra) element.appendChild(extra)

  /*
   * A tooltip already on screen glides to the next point. One that is appearing is placed without
   * the transition first, so it does not fly in from wherever the pointer left it.
   */
  if (!wasVisible) {
    element.style.transitionProperty = 'opacity'
    positionTooltip(element, chart, tooltip)
    flushLayout(element)
    element.style.transitionProperty = ''
  } else {
    positionTooltip(element, chart, tooltip)
  }

  element.style.opacity = '1'
}

/*
 * The canvas cannot read CSS, so the page font and its 14px size are passed in. A fresh object per
 * config, as the line and bar options would otherwise share one.
 */
const getAxisScales = () => {
  const ticks = {
    color: getCssRgbValue('--color-text-tertiary'),
    font: {
      family:
        typeof document === 'undefined'
          ? undefined
          : getComputedStyle(document.documentElement).getPropertyValue('--font-inter').trim() ||
            undefined,
      size: 14,
    },
  }

  return {
    x: {
      border: { display: false },
      grid: { display: false },
      ticks: { ...ticks, maxRotation: 0, autoSkipPadding: 16 },
    },
    y: {
      min: 0,
      border: { display: false, dash: [4, 4] },
      grid: {
        color: getCssRgbValue('--color-border-100'),
        drawTicks: false,
      },
      ticks: { ...ticks, maxTicksLimit: 5, padding: 8 },
    },
  }
}

const LINE_WIDTH = 2.5

export const lineChartOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { right: MARKER_CLEARANCE } },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  /* Chart.js fades hover styles over a second by default, which reads as lag under the cursor. */
  transitions: { active: { animation: { duration: 150 } } },
  elements: {
    point: {
      /*
       * `activePointPlugin` draws the hovered marker, so the point element itself never paints.
       * `backgroundColor` is only the color the tooltip marker reads for this series.
       */
      radius: 0,
      hoverRadius: 0,
      backgroundColor: getCssRgbValue('--color-icon-accent'),
    },
    line: {
      cubicInterpolationMode: 'monotone',
      borderWidth: LINE_WIDTH,
      borderColor: getCssRgbValue('--color-icon-accent'),
      backgroundColor: getLineGradient,
      fill: true,
    },
  },
  /*
   * With an explicit `min` on the y scale Chart.js clips the line to the plot area, which halves the
   * stroke wherever it runs along zero or peaks on the top tick. Half the stroke may overflow.
   */
  datasets: { line: { clip: LINE_WIDTH / 2 } },
  plugins: {
    tooltip: {
      enabled: false,
      /*
       * Chart.js animates `caretX` / `caretY` itself but hands them to `external` only on pointer
       * events, so an animated model leaves the node lagging wherever the pointer stopped. The
       * model jumps to the active point and the CSS transition does the gliding.
       */
      animation: false,
      external: externalTooltipHandler,
    },
    legend: { display: false },
  },
  scales: getAxisScales(),
}

export const barChartOptions: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  transitions: { active: { animation: { duration: 150 } } },
  /*
   * `barSlotPlugin` rewrites x and width after each update, and a running animation would keep
   * moving them back toward chart.js's own layout.
   */
  animations: { x: { duration: 0 }, width: { duration: 0 } },
  datasets: { bar: { categoryPercentage: 1, barPercentage: 1 } },
  elements: {
    bar: {
      backgroundColor: getCssRgbValue('--color-system-primary-400'),
      hoverBackgroundColor: getCssRgbValue('--color-system-primary-500'),
      borderRadius: getBarRadius,
      /* Chart.js widens touching bars to hide seams that pixel-snapped edges do not have. */
      inflateAmount: 0,
    },
  },
  plugins: {
    tooltip: {
      enabled: false,
      /*
       * Chart.js animates `caretX` / `caretY` itself but hands them to `external` only on pointer
       * events, so an animated model leaves the node lagging wherever the pointer stopped. The
       * model jumps to the active point and the CSS transition does the gliding.
       */
      animation: false,
      external: externalTooltipHandler,
    },
    legend: { display: false },
  },
  scales: getAxisScales(),
}

/*
 * The axis stays compact while the tooltip names the full date, so a chart passes the long titles
 * next to its labels. Anything without a title falls back to the axis label.
 */
export const withTooltipTitles = <TType extends DashboardChartType>(
  options: NonNullable<ChartOptions<TType>>,
  titles: string[],
): ChartOptions<TType> => ({
  ...options,
  plugins: {
    ...options.plugins,
    tooltip: {
      ...options.plugins?.tooltip,
      callbacks: {
        ...options.plugins?.tooltip?.callbacks,
        title: (items: Array<{ dataIndex: number; label: string }>) => {
          const [item] = items

          if (!item) return ''

          return titles[item.dataIndex] ?? item.label
        },
      },
    },
  },
})

export const getChartData = <TType extends DashboardChartType>({
  labels,
  data,
}: {
  labels: string[]
  data: number[]
}): ChartData<TType, number[], string> =>
  ({ labels, datasets: [{ data }] }) as ChartData<TType, number[], string>
